import * as React from 'react';
import { Check, ChevronsUpDown, Search, Building2, X, Filter, Star, Clock, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { CostCentre } from '@/types';
import { MultiSelect } from '@/components/ui/multiselect';
import { PROJECT_STATUSES } from '@/lib/statuses';
import { useScheduler } from '@/contexts/SchedulerContext';
import { useCostCentreSelector } from '@/hooks/use-cost-centre-selector';
import { CostCentreSearchModal } from './CostCentreSearchModal';

interface EnhancedCostCentreSelectProps {
  costCentres: CostCentre[];
  allCostCentres?: CostCentre[];
  selectedCostCentreId?: string;
  onSelect: (costCentreId: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  projectId?: string;
}

export function EnhancedCostCentreSelect({
  costCentres,
  allCostCentres,
  selectedCostCentreId,
  onSelect,
  disabled = false,
  placeholder = 'Select cost centre...',
  className,
  projectId,
}: EnhancedCostCentreSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedStatuses, setSelectedStatuses] = React.useState<string[]>([]);
  const [showAllMode, setShowAllMode] = React.useState(false);
  const { settings } = useScheduler();

  // Determine which cost centres to display: project-filtered or all
  const hasProjectFilter = !!projectId && !!allCostCentres && allCostCentres.length > costCentres.length;
  const activeCostCentres = showAllMode && allCostCentres ? allCostCentres : costCentres;

  // Reset "Show All" mode when project changes
  React.useEffect(() => {
    setShowAllMode(false);
  }, [projectId]);

  const {
    enrichedCostCentres,
    favouriteCostCentres,
    recentCostCentres,
    toggleFavourite,
    recordCostCentreAccess,
    isFavourite,
  } = useCostCentreSelector(activeCostCentres, projectId);

  const selectedCostCentre = (allCostCentres || costCentres).find((c) => String(c.id) === String(selectedCostCentreId));

  const availableStatuses = React.useMemo(() => {
    const configuredStatuses = settings.availableCostCentreStatuses === 'all' 
      ? PROJECT_STATUSES.map(s => s.value)
      : settings.availableCostCentreStatuses;
    
    return PROJECT_STATUSES.filter(status => 
      configuredStatuses.includes(status.value)
    );
  }, [settings.availableCostCentreStatuses]);

  React.useEffect(() => {
    if (selectedStatuses.length === 0) {
      setSelectedStatuses(availableStatuses.map(s => s.value));
    }
  }, [availableStatuses]);

  const filteredCostCentres = React.useMemo(() => {
    let filtered = activeCostCentres;

    const statusesToFilter = selectedStatuses.length > 0 
      ? selectedStatuses 
      : availableStatuses.map(s => s.value);
    
    filtered = filtered.filter(costCentre => 
      statusesToFilter.includes(costCentre.status)
    );

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (costCentre) =>
          costCentre.name.toLowerCase().includes(query) ||
          costCentre.code.toLowerCase().includes(query) ||
          costCentre.department.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [activeCostCentres, searchQuery, selectedStatuses, availableStatuses]);

  const sortedCostCentres = React.useMemo(() => {
    return [...filteredCostCentres].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [filteredCostCentres]);

  const handleSelect = (costCentreId: string) => {
    onSelect(costCentreId);
    recordCostCentreAccess(costCentreId);
    setOpen(false);
    setSearchQuery('');
  };

  const handleModalSelect = (costCentreId: string) => {
    onSelect(costCentreId);
    recordCostCentreAccess(costCentreId);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect('');
  };

  // Show up to 5 favorites in the dropdown
  const popoverFavourites = favouriteCostCentres.slice(0, 5);
  
  // Show exactly 5 recent cost centres, excluding those already shown in favorites
  const popoverRecents = React.useMemo(() => {
    const favouriteIds = new Set(popoverFavourites.map(cc => String(cc.id)));
    return recentCostCentres
      .filter(cc => !favouriteIds.has(String(cc.id)))
      .slice(0, 5);
  }, [recentCostCentres, popoverFavourites]);

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'w-full justify-start h-8 text-xs px-3',
              !selectedCostCentre && 'text-muted-foreground',
              className
            )}
          >
            <div className="flex items-center gap-3 w-full">
              {selectedCostCentre ? (
                <>
                  <div className="flex-1 min-w-0 text-left">
                    <span className="font-medium text-sm truncate block">
                      {selectedCostCentre.name} ({selectedCostCentre.code})
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 shrink-0 hover:bg-destructive/10"
                    onClick={handleClear}
                    type="button"
                  >
                    <X className="h-4 w-4 text-muted-foreground hover:text-destructive transition-colors" />
                  </Button>
                </>
              ) : (
                <span className="flex-1 text-left">{placeholder}</span>
              )}
              <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[500px] p-0" align="start">
          <Command shouldFilter={false}>
            <div className="flex items-center border-b px-3">
              <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
              <input
                placeholder="Search by name, code, or department..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            
            <div className="border-b px-3 py-3 space-y-2">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 opacity-50" />
                <span className="text-sm font-medium">Filter by Status</span>
              </div>
              <MultiSelect
                options={availableStatuses}
                value={selectedStatuses}
                onValueChange={setSelectedStatuses}
                placeholder="Select statuses..."
                maxDisplay={3}
              />
              {selectedStatuses.length < availableStatuses.length && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedStatuses(availableStatuses.map(s => s.value))}
                  className="h-7 text-xs"
                >
                  Clear filters
                </Button>
              )}
            </div>

            <CommandList className="max-h-[400px]">
              {popoverFavourites.length > 0 && (
                <CommandGroup>
                  <div className="flex items-center gap-2 px-3 py-2">
                    <Star className="h-3.5 w-3.5 text-yellow-500" />
                    <span className="text-xs font-semibold uppercase text-muted-foreground">Favourites</span>
                  </div>
                  {popoverFavourites.map((cc) => (
                    <CommandItem
                      key={`fav-${cc.id}`}
                      value={`fav-${cc.id}`}
                      onSelect={() => handleSelect(String(cc.id))}
                      className="px-3 py-2 cursor-pointer"
                    >
                      <div className="flex items-center gap-3 w-full">
                        <Star
                          className="h-4 w-4 shrink-0 fill-yellow-400 text-yellow-400 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavourite(String(cc.id));
                          }}
                        />
                        <span className="font-medium text-sm truncate flex-1">
                          {cc.name} ({cc.code})
                        </span>
                        <Check
                          className={cn(
                            'h-4 w-4 shrink-0',
                            String(selectedCostCentreId) === String(cc.id) ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {popoverRecents.length > 0 && (
                <CommandGroup>
                  <div className="flex items-center gap-2 px-3 py-2">
                    <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs font-semibold uppercase text-muted-foreground">Recently Used</span>
                  </div>
                  {popoverRecents.map((cc) => (
                    <CommandItem
                      key={`recent-${cc.id}`}
                      value={`recent-${cc.id}`}
                      onSelect={() => handleSelect(String(cc.id))}
                      className="px-3 py-2 cursor-pointer"
                    >
                      <div className="flex items-center gap-3 w-full">
                        <Star
                          className={cn(
                            'h-4 w-4 shrink-0 cursor-pointer transition-colors',
                            isFavourite(String(cc.id))
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-muted-foreground hover:text-yellow-400'
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavourite(String(cc.id));
                          }}
                        />
                        <span className="font-medium text-sm truncate flex-1">
                          {cc.name} ({cc.code})
                        </span>
                        <Check
                          className={cn(
                            'h-4 w-4 shrink-0',
                            String(selectedCostCentreId) === String(cc.id) ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {(popoverFavourites.length > 0 || popoverRecents.length > 0) && (
                <div className="px-3 py-1">
                  <Separator />
                </div>
              )}

              <CommandEmpty>
                <div className="py-6 text-center">
                  <Building2 className="mx-auto h-8 w-8 text-muted-foreground/50 mb-2" />
                  <p className="text-sm text-muted-foreground">No cost centres found</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Try adjusting your search
                  </p>
                </div>
              </CommandEmpty>
              <CommandGroup>
                {sortedCostCentres.map((costCentre) => (
                  <CommandItem
                    key={String(costCentre.id)}
                    value={String(costCentre.id)}
                    onSelect={() => handleSelect(String(costCentre.id))}
                    className="px-3 py-3 cursor-pointer"
                  >
                    <div className="flex items-center gap-3 w-full">
                      <Star
                        className={cn(
                          'h-4 w-4 shrink-0 cursor-pointer transition-colors',
                          isFavourite(String(costCentre.id))
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-muted-foreground hover:text-yellow-400'
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavourite(String(costCentre.id));
                        }}
                      />
                      <span className="font-medium text-sm truncate flex-1">
                        {costCentre.name} ({costCentre.code})
                      </span>
                      <Check
                        className={cn(
                          'h-4 w-4 shrink-0',
                          String(selectedCostCentreId) === String(costCentre.id) ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>

            <div className="border-t p-2 flex flex-col gap-1">
              {hasProjectFilter && (
                <Button
                  variant="ghost"
                  className="w-full justify-center text-sm"
                  onClick={() => setShowAllMode(!showAllMode)}
                >
                  <Filter className="mr-2 h-4 w-4" />
                  {showAllMode ? 'Show Project Only' : 'Show All'}
                </Button>
              )}
              <Button
                variant="ghost"
                className="w-full justify-center text-sm"
                onClick={() => {
                  setOpen(false);
                  setModalOpen(true);
                }}
              >
                <ArrowRight className="mr-2 h-4 w-4" />
                Show More...
              </Button>
            </div>
          </Command>
        </PopoverContent>
      </Popover>

      <CostCentreSearchModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        costCentres={enrichedCostCentres}
        value={selectedCostCentreId}
        onSelect={handleModalSelect}
        onToggleFavourite={toggleFavourite}
        isFavourite={isFavourite}
        selectedCostCentreId={selectedCostCentreId}
      />
    </>
  );
}
